import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { BackgroundColor, FontSize, TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Underline from "@tiptap/extension-underline";

const SesiImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-align": {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") || "center",
        renderHTML: (attributes) => ({ "data-align": attributes["data-align"] || "center" }),
      },
      "data-width": {
        default: "100",
        parseHTML: (element) => element.getAttribute("data-width") || "100",
        renderHTML: (attributes) => ({ "data-width": attributes["data-width"] || "100" }),
      },
    };
  },
});

function filesFromTransfer(transfer) {
  return [...(transfer?.files || [])].filter((file) => file.type.startsWith("image/"));
}

function createExtensions() {
  return [
    StarterKit.configure({ link: false, underline: false }),
    Underline,
    TextStyle,
    Color,
    BackgroundColor,
    FontFamily,
    FontSize,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
    SesiImage.configure({ allowBase64: true, HTMLAttributes: { class: "editor-image" } }),
  ];
}

class RichEditorAdapter {
  constructor(options) {
    this.element = options.element;
    this.originalHtml = String(options.content || "");
    this.touched = false;
    this.onUpdate = options.onUpdate || (() => {});
    this.onFocus = options.onFocus || (() => {});
    this.onSelectionUpdate = options.onSelectionUpdate || (() => {});
    this.onImageFiles = options.onImageFiles || (() => {});
    this.element.dataset.placeholder = options.placeholder || "Digite o conteúdo...";
    this.editor = new Editor({
      element: this.element,
      extensions: createExtensions(),
      content: this.originalHtml,
      editorProps: {
        attributes: {
          class: "ProseMirror sesi-tiptap-content",
          role: "textbox",
          "aria-multiline": "true",
          "aria-label": options.ariaLabel || "Editor de texto",
          "data-placeholder": options.placeholder || "Digite o conteúdo...",
        },
        handlePaste: (_view, event) => {
          const files = filesFromTransfer(event.clipboardData);
          if (!files.length) return false;
          event.preventDefault();
          this.onImageFiles(files, this);
          return true;
        },
        handleDrop: (_view, event) => {
          const files = filesFromTransfer(event.dataTransfer);
          if (!files.length) return false;
          event.preventDefault();
          this.onImageFiles(files, this);
          return true;
        },
      },
      onFocus: () => this.onFocus(this),
      onSelectionUpdate: () => this.onSelectionUpdate(this),
      onUpdate: () => {
        this.touched = true;
        this.updateEmptyState();
        this.onUpdate(this);
      },
    });
    this.updateEmptyState();
  }

  updateEmptyState() {
    this.editor.view.dom.dataset.empty = String(this.editor.isEmpty);
  }

  getHTML({ preserveUntouched = true } = {}) {
    return preserveUntouched && !this.touched ? this.originalHtml : this.editor.getHTML();
  }

  getText() {
    return this.editor.getText({ blockSeparator: "\n" });
  }

  getCounts() {
    const text = this.getText().trim();
    return {
      characters: text.length,
      words: text ? text.split(/\s+/u).length : 0,
    };
  }

  focus(position) {
    this.editor.commands.focus(position);
  }

  markTouched() {
    this.touched = true;
    this.onUpdate(this);
  }

  insertImage(attributes) {
    this.editor.chain().focus().setImage({
      src: attributes.src,
      alt: attributes.alt || "Imagem do item",
      title: attributes.title || null,
      "data-align": attributes.align || "center",
      "data-width": String(attributes.width || "100"),
    }).run();
  }

  imageIsActive() {
    return this.editor.isActive("image");
  }

  selectedImage() {
    return this.imageIsActive() ? this.editor.getAttributes("image") : null;
  }

  updateImage(attributes) {
    if (!this.imageIsActive()) return false;
    return this.editor.chain().focus().updateAttributes("image", attributes).run();
  }

  removeSelectedImage() {
    if (!this.imageIsActive()) return false;
    return this.editor.chain().focus().deleteSelection().run();
  }

  isActive(action, attributes = {}) {
    const marks = {
      bold: "bold",
      italic: "italic",
      underline: "underline",
      strike: "strike",
      bulletList: "bulletList",
      orderedList: "orderedList",
      blockquote: "blockquote",
      link: "link",
      image: "image",
    };
    if (marks[action]) return this.editor.isActive(marks[action], attributes);
    if (action === "paragraph") return this.editor.isActive("paragraph");
    if (action === "heading2") return this.editor.isActive("heading", { level: 2 });
    if (action === "heading3") return this.editor.isActive("heading", { level: 3 });
    if (action.startsWith("align")) return this.editor.isActive({ textAlign: action.replace("align", "").toLowerCase() });
    return false;
  }

  attributes(type) {
    return this.editor.getAttributes(type);
  }

  can(action) {
    if (action === "undo") return this.editor.can().chain().focus().undo().run();
    if (action === "redo") return this.editor.can().chain().focus().redo().run();
    return true;
  }

  run(action, value) {
    const chain = this.editor.chain().focus();
    const actions = {
      undo: () => chain.undo().run(),
      redo: () => chain.redo().run(),
      paragraph: () => chain.setParagraph().run(),
      heading2: () => chain.toggleHeading({ level: 2 }).run(),
      heading3: () => chain.toggleHeading({ level: 3 }).run(),
      blockquote: () => chain.toggleBlockquote().run(),
      bold: () => chain.toggleBold().run(),
      italic: () => chain.toggleItalic().run(),
      underline: () => chain.toggleUnderline().run(),
      strike: () => chain.toggleStrike().run(),
      bulletList: () => chain.toggleBulletList().run(),
      orderedList: () => chain.toggleOrderedList().run(),
      alignLeft: () => chain.setTextAlign("left").run(),
      alignCenter: () => chain.setTextAlign("center").run(),
      alignRight: () => chain.setTextAlign("right").run(),
      fontFamily: () => value ? chain.setFontFamily(value).run() : chain.unsetFontFamily().run(),
      fontSize: () => value ? chain.setFontSize(value).run() : chain.unsetFontSize().run(),
      color: () => value ? chain.setColor(value).run() : chain.unsetColor().run(),
      backgroundColor: () => value ? chain.setBackgroundColor(value).run() : chain.unsetBackgroundColor().run(),
      unsetLink: () => chain.unsetLink().run(),
    };
    return actions[action]?.() ?? false;
  }

  setLink(href) {
    if (!href) return this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return this.editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  }

  destroy() {
    this.editor.destroy();
  }
}

window.SesiTiptap = {
  create(options) {
    return new RichEditorAdapter(options);
  },
};
